import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Goal } from '@feathr/blackbox';
import { ConfirmModal, toast } from '@feathr/components';
import { errorMessage, logError } from '@feathr/hooks';

interface IUnarchiveGoalModalProps {
  goal: Goal;
  onClose: () => void;
  opened: boolean;
}

function UnarchiveGoalModal({
  goal,
  onClose,
  opened,
}: Readonly<IUnarchiveGoalModalProps>): JSX.Element {
  const { t } = useTranslation();
  const name = goal.get('name');

  async function unarchive(): Promise<void> {
    try {
      await goal.patch({ is_archived: false });
      // rachis's patch sets isErrored/error rather than rejecting
      if (goal.isErrored) {
        throw goal.error;
      }
      toast(t("Unarchived '{{- name}}'.", { name }), { type: 'success' });
      onClose();
      goal.collection?.refreshApiCache();
    } catch (error) {
      logError({
        extra: { goalId: goal.id, message: errorMessage(error, t) },
        level: 'error',
        message: 'Failed to unarchive goal',
      });
      toast(t("Couldn't unarchive goal. Try again."), { type: 'error' });
    }
  }

  return (
    <ConfirmModal
      confirmButtonText={t('Unarchive')}
      confirmButtonType={'primary'}
      description={t(
        'Unarchiving makes this goal available for selection on new campaigns and flights, and brings it back into reports.',
      )}
      onClose={onClose}
      onConfirm={unarchive}
      opened={opened}
      t={t}
      title={t("Unarchive '{{- name}}'?", { name })}
    />
  );
}

export default UnarchiveGoalModal;
